/**
 * Experimental, DIAGNOSTIC-ONLY visible chin contour.
 *
 * This module answers one architecture question with data instead of opinion:
 * *can the visible lower edge of the chin/beard be measured separately from the
 * mesh landmark 152?*
 *
 * It is deliberately **not wired into any measurement**: `chinPoint`,
 * `headHeight`, `headHeightRatio`, every check and every verdict keep using
 * landmark 152 exactly as before. The estimate travels through the metrics as an
 * optional field, is printed by `?debug=1` and is compared against the manual
 * ground truth by the calibration suite. Nothing reads it to decide anything.
 *
 * Why it can work at all (measured on `tests/geometry/real`, 20 photographs with
 * manual ground truth):
 *
 *  - **shadow** — under the chin there is usually a shadow band: luma drops by
 *    9–28 units below the chin row and then recovers towards the skin value. The
 *    manual chin sits where the recovery happens.
 *  - **beard** — a grey/white beard is *desaturated* (HSV S ≈ 0.13–0.15) while
 *    skin is reddish (S ≈ 0.36–0.45). The manual chin sits where saturation
 *    returns to skin level.
 *
 * Both branches are gated so that a smooth beard gradient (which never returns to
 * the baseline) cannot be mistaken for a shadow, and a clean chin (no dip at all)
 * always keeps the landmark.
 */

export type VisibleChinSource = 'shadow' | 'beard' | 'landmark' | 'none';

export interface VisibleChinInput {
  /** Luma plane of the analysis buffer. */
  luma: Uint8ClampedArray;
  /** Colour plane of the analysis buffer (1 or 3 channels). */
  data: Uint8ClampedArray;
  channels: number;
  width: number;
  height: number;
  /** Normalized y of landmark 152 — the chin the product currently measures. */
  chinY: number;
  /** Normalized x of the face centre; the scan band is centred on it. */
  faceCenterX: number;
}

export interface VisibleChinEstimate {
  /** Normalized y of the visible chin bottom (= `chinY` when nothing was found). */
  y: number;
  source: VisibleChinSource;
  /** 0…1 — how much evidence the detection had. */
  confidence: number;
  /** Depth of the under-chin luma dip, in luma units (0 when there is none). */
  shadowDepth: number;
  /** Mean HSV saturation at the landmark row. */
  saturationAt152: number;
  /** Mean HSV saturation at the detected row (NaN when nothing was found). */
  saturationRecovery: number;
  /** Why the estimate is what it is (shown in the debug panel). */
  note: string;
}

/**
 * Thresholds of the diagnostic rule. Tuned on the 20 real photographs of the
 * calibration set — see `tests/geometry/real/reports/chin-beard-research.md`;
 * an independent validation set is still required before this may influence a
 * verdict.
 */
export const VISIBLE_CHIN_OPTIONS = {
  /** Minimum luma dip below the landmark row to call it an under-chin shadow. */
  shadowDepth: 8,
  /** The recovery must reach `base − recoveryFraction × depth`. */
  recoveryFraction: 0.2,
  /** Search windows below the landmark, as a share of image height. */
  shadowWindow: 0.08,
  beardWindow: 0.12,
  /** Saturation at the landmark row at or below which the region is "not skin". */
  saturationBeard: 0.3,
  /** Saturation at which skin is considered to have resumed. */
  saturationSkin: 0.34,
  /** Rows that must agree before a boundary counts (noise guard). */
  confirmRows: 3,
  /** Band half-width around the face centre, as a share of image width. */
  bandHalfWidth: 0.04,
} as const;

const mean = (values: readonly number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;

const smooth3 = (values: readonly number[]): number[] =>
  values.map((_, index) =>
    mean(values.slice(Math.max(0, index - 1), Math.min(values.length, index + 2))),
  );

/** HSV saturation of one buffer pixel: (max − min) / max, 0 for black. */
const saturationOf = (
  data: Uint8ClampedArray,
  channels: number,
  width: number,
  x: number,
  y: number,
): number => {
  if (channels < 3) return 0;
  const index = (y * width + x) * channels;
  const r = data[index] as number;
  const g = data[index + 1] as number;
  const b = data[index + 2] as number;
  const max = Math.max(r, g, b);
  if (max <= 0) return 0;
  return (max - Math.min(r, g, b)) / max;
};

/**
 * Estimates the visible chin contour. Pure function over the analysis buffer: it
 * cannot change a measurement, it only reports where the contour appears to be.
 */
export const estimateVisibleChin = (input: VisibleChinInput): VisibleChinEstimate => {
  const { luma, data, channels, width, height } = input;
  const nothing = (note: string, shadowDepth = 0, saturationAt152 = 0): VisibleChinEstimate => ({
    y: input.chinY,
    source: 'landmark',
    confidence: 0,
    shadowDepth,
    saturationAt152,
    saturationRecovery: Number.NaN,
    note,
  });

  if (width <= 0 || height <= 0 || luma.length < width * height || data.length === 0) {
    return { ...nothing('нет пиксельного буфера'), source: 'none' };
  }

  const options = VISIBLE_CHIN_OPTIONS;
  const y152 = Math.round(input.chinY * height);
  if (y152 < 0 || y152 >= height) return { ...nothing('landmark вне кадра'), source: 'none' };

  const halfWidth = Math.max(4, Math.round(options.bandHalfWidth * width));
  const centreX = Math.min(Math.max(Math.round(input.faceCenterX * width), 0), width - 1);
  const x0 = Math.max(0, centreX - halfWidth);
  const x1 = Math.min(width - 1, centreX + halfWidth);

  const lastRow = Math.min(height - 1, y152 + Math.round(0.35 * height));
  const lumaRows: number[] = [];
  const satRows: number[] = [];
  for (let y = y152; y <= lastRow; y += 1) {
    const lumaValues: number[] = [];
    const satValues: number[] = [];
    for (let x = x0; x <= x1; x += 1) {
      lumaValues.push(luma[y * width + x] as number);
      satValues.push(saturationOf(data, channels, width, x, y));
    }
    lumaRows.push(mean(lumaValues));
    satRows.push(mean(satValues));
  }
  if (lumaRows.length < options.confirmRows * 2) {
    return nothing('короткий профиль', 0, satRows[0] ?? 0);
  }

  const profile = smooth3(lumaRows);
  const saturation = smooth3(satRows);
  const saturationAt152 = saturation[0] as number;
  const windowEnd = Math.min(profile.length - 1, Math.round(options.shadowWindow * height));
  const base = profile[0] as number;

  let minIndex = 0;
  for (let i = 0; i <= windowEnd; i += 1) {
    if ((profile[i] as number) < (profile[minIndex] as number)) minIndex = i;
  }
  const depth = base - (profile[minIndex] as number);

  const confirmedFrom = (values: number[], from: number, test: (value: number) => boolean): boolean =>
    from + options.confirmRows <= values.length &&
    values
      .slice(from, from + options.confirmRows)
      .every((value) => test(value ?? Number.NEGATIVE_INFINITY));

  // --- branch 1: under-chin shadow — the profile returns close to its baseline
  if (depth >= options.shadowDepth && minIndex > 0) {
    const target = base - options.recoveryFraction * depth;
    for (let i = minIndex; i <= windowEnd && i < profile.length; i += 1) {
      if (!confirmedFrom(profile, i, (value) => value >= target)) continue;
      return {
        y: (y152 + i) / height,
        source: 'shadow',
        confidence: Math.min(1, depth / 25),
        shadowDepth: depth,
        saturationAt152,
        saturationRecovery: saturation[i] as number,
        note: `тень под подбородком: ${depth.toFixed(0)} luma, возврат к базовой яркости`,
      };
    }
  }

  // --- branch 2: desaturated (grey/white) beard — saturation returns to skin
  if (saturationAt152 <= options.saturationBeard) {
    const beardEnd = Math.min(profile.length - 1, Math.round(options.beardWindow * height));
    for (let i = 0; i <= beardEnd; i += 1) {
      if (!confirmedFrom(saturation, i, (value) => value >= options.saturationSkin)) continue;
      return {
        y: (y152 + i) / height,
        source: 'beard',
        confidence: Math.min(1, (options.saturationSkin - saturationAt152) / 0.1),
        shadowDepth: depth,
        saturationAt152,
        saturationRecovery: saturation[i] as number,
        note: `седая борода: насыщенность ${saturationAt152.toFixed(2)} → ${(saturation[i] as number).toFixed(2)}`,
      };
    }
  }

  return nothing(`видимого контура ниже нет (долина ${depth.toFixed(0)} luma)`, depth, saturationAt152);
};
